<template>
  <li class="mb-12">
    <RevPicker
      v-if="step.id === 'color'"
      :aria-disabled="shouldDisplayDisabledState"
      :class="[$style.pickerColorOverride, 'h-full']"
      :description="description"
      :disabled="shouldDisplayDisabledState"
      :label="label"
      :loading="loading"
      :prefix="icon(option)"
      :prefix-props="iconProps(option)"
      :selected="option.selected"
      :tag="tag"
      :to="!shouldDisplayDisabledState ? option.link : null"
      @click="onClick"
    />
    <LargePicker
      v-if="step.id !== 'color'"
      :disabled="shouldDisplayDisabledState"
      :index="index"
      :label="label"
      :loading="loading"
      :price="description"
      :selected="option.selected"
      :sub-label="subLabel"
      :tag="tag"
      :to="!shouldDisplayDisabledState ? option.link : undefined"
      @click="onClick"
    />
  </li>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import { cartAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { openModal } from '@ds/components/ModalBase'
import { RevPicker } from '@ds/components/Picker'
import { type Variants as TagVariants } from '@ds/components/Tag'

import { MODAL_NAMES } from '~/scopes/buyback/constants'
import { useSwapStore } from '~/scopes/buyback/swap/stores/swap'

import ColorRound from '../../../Pickers/components/Picker/ColorRound/ColorRound.vue'
import { type Step } from '../../utils/createCustomizationFunnelData'
import LargePicker from '../LargePicker/LargePicker.vue'

import translations from './StepOption.translations'

const { trackClick } = useTracking()
const i18n = useI18n()
const { resetEstimations } = useSwapStore()
const loading = ref(false)

const props = withDefaults(
  defineProps<{
    step: Step
    option: Step['options'][number]
    index: number
    isGroupLoading?: boolean
  }>(),
  {
    isGroupLoading: false,
  },
)

const emit = defineEmits<{
  click: [Step['options'][number], Step]
  changed: [Step['options'][number], Step]
}>()

// When we click on a picker, the `loading` state is added manually. Behind the scene, we're triggering
// a /pickers endpoint call, so let's remove the loading state once the picker status change to selected
// (as it's backend driven)
watch(
  () => props.option.selected,
  () => {
    if (loading.value && props.option.selected) {
      emit('changed', props.option, props.step)
      loading.value = false
    }
  },
)

const description = computed(() => {
  if (props.step.id === 'battery' && !props.option.available) {
    return i18n(translations.comingSoon)
  }

  if (!props.option.available) {
    return i18n(translations.soldOut)
  }

  if (!props.option.price) {
    return ''
  }

  // For trade-in, the price is an already formatted string :'(
  if (typeof props.option.price === 'string') {
    return props.option.price
  }

  return i18n.price(props.option.price)
})

const label = computed(() => {
  return typeof props.option.label === 'string'
    ? props.option.label
    : i18n(props.option.label)
})

const subLabel = computed(() => {
  return typeof props.option.subLabel === 'object'
    ? i18n(props.option.subLabel)
    : props.option.subLabel
})

const tag = computed(() => {
  if (props.option.tag) {
    return {
      label:
        typeof props.option.tag === 'object'
          ? i18n(props.option.tag)
          : props.option.tag,
      variant: 'primary' as TagVariants,
    }
  }

  if (!props.option.goodDeal) {
    return '' as string
  }

  return {
    label: i18n(translations.goodDeal),
    variant: 'primary' as TagVariants,
  }
})

const shouldDisplayDisabledState = computed(() => {
  return !props.option.available || (!loading.value && props.isGroupLoading)
})

function icon(option: Step['options'][number]) {
  if (option.color) {
    return ColorRound
  }

  return null
}

function iconProps(option: Step['options'][number]) {
  if (option.color) {
    return { color: option.color, class: { 'flex-shrink-0': true } }
  }

  return {}
}

async function onClick() {
  if (!props.option.available || loading.value || props.isGroupLoading) {
    return false
  }

  if (props.step.id === 'battery') {
    // We have an exception on new battery for now, to be compatible with the legacy event we have on the
    // toggle in the previous pickers.
    trackClick({
      zone: props.step.trackingId ?? '',
      name: props.step.trackingId ?? 'unknown_picker',
      value: props.option.trackingValue ?? '',
    })
  } else {
    // We're a bit defensive on those trackingId / trackingValue, as the API specs says that
    // both fields are optional. Let's have default values to make sure we're able to detect this
    // in amplitude no matter what
    trackClick({
      zone: props.step.trackingId ?? 'unknown_picker',
      name: props.option.trackingValue ?? label.value,
    })
  }

  // // Custom work for the trade in step as we don't refresh anything from the pickers endpoint
  if (props.step.id === 'tradein') {
    // Let's open the modal by clicking Yes
    if (props.option.value === 'on') {
      openModal(MODAL_NAMES.SWAP_MIX_AND_MATCH)
    }
    // Remove the offer from the cart if the users selects no after accepting a swap offer
    if (props.option.value === 'off' && !props.option.selected) {
      await $httpFetch(cartAPI.deleteSwap)
      resetEstimations()
    }

    return false
  }

  emit('click', props.option, props.step)

  // If the field is already selected, we won't change the URL nor refresh the network calls, so
  // let's directly trigger the changed event and skip the loading state
  if (props.option.selected) {
    emit('changed', props.option, props.step)
  } else {
    loading.value = true
  }

  return true
}
</script>

<style module>
.pickerColorOverride > div > div:last-child {
  @apply pt-1;
}
</style>
